import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import Select from 'react-select';
import moment from 'moment/moment';

import { _campaignDelete, _campaignsGet, _getTimeZones } from '../../services/apiConnections.service';
import { ModalDialog } from '../elements/ModalDialog.component';
import { EditLink } from '../elements/EditLink.component';

export const Campaigns = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [token] = useState(localStorage.getItem('token'));
    const [error, setError] = useState(null);
    const [maxStartTime, setMaxStartTime] = useState(new Date());
    const [timeZones, setTimeZones] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState({});
    const [stateFilter, setStateFilter] = useState({
        label: 'All',
        value: -1
    });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [timeZone, setTimeZone] = useState({
        label: Intl.DateTimeFormat().resolvedOptions().timeZone,
        value: -1
    });
    const [dateFrom, setDateFrom] = useState(
        new Date(
            new Date(
                new Date().setHours(0)
            ).setMinutes(0)
        )
    );
    const [dateTo, setDateTo] = useState(new Date());
    const [totalItems, setTotalItems] = useState(0);

    const startOfTheDay = new Date(new Date().setHours(0)).setMinutes(0);
    let browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const states = [
        { label: 'All', value: -1 },
        { label: 'Draft', value: 0 },
        { label: 'Active', value: 1 },
        { label: 'In Progress', value: 2 },
        { label: 'Finished', value: 3 }
    ];

    const defaultSorted = [{
        dataField: 'name',
        order: 'asc'
    }];

    useEffect(() => {
        if (token) {
            handleInitialLoad();
        }
    }, [token]);

    useEffect(() => {
        if (token && timeZone.label) {
            fetchData({
                sortField: defaultSorted[0].dataField,
                sortOrder: defaultSorted[0].order
            });
        }
    }, [dateFrom, dateTo, timeZone]);

    useEffect(() => {
        let now = new Date();
        const fromDate = new Date(dateFrom);
        if (fromDate.getDate() < now.getDate()) {
            setMaxStartTime(new Date(new Date().setHours(23, 59)));
        } else {
            setMaxStartTime(new Date());
        }
        if (fromDate > dateTo) {
            setDateTo(fromDate);
        }
    }, [dateFrom, dateTo]);

    const handleInitialLoad = async () => {
        try {
            // Get timezones first
            const result = await _getTimeZones(token);
            const arr = result.time_zones.map((e) => ({
                label: e.name,
                value: result.time_zones.indexOf(e)
            }));
            setTimeZones(arr);

            // Set timezone and fetch data
            const currentTimeZone = arr.find((e) => e.label === browserTimeZone) || {
                label: browserTimeZone,
                value: -1
            };
            setTimeZone(currentTimeZone);

            // Initial data fetch
            const params = {
                page,
                page_size: pageSize,
                date_from: dateToLocalISO(dateFrom) || null,
                date_to: dateToLocalISO(dateTo) || null,
                time_zone: currentTimeZone.label,
                status: stateFilter.value,
                sort: defaultSorted[0].dataField,
                order: defaultSorted[0].order
            };

            const campaignsResult = await _campaignsGet(token, params);
            if (campaignsResult.error) {
                setError(campaignsResult.error);
            } else {
                setCampaigns(campaignsResult.campaigns);
                setTotalItems(campaignsResult.total_items);
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDateChange = (date, isFrom = true) => {
        if (isFrom) {
            setDateFrom(new Date(date));
        } else {
            setDateTo(new Date(date));
        }
    };

    const handleDateRangeChange = (fromDate, toDate) => {
        setDateFrom(fromDate);
        setDateTo(toDate);
        fetchData({
            sortField: defaultSorted[0].dataField,
            sortOrder: defaultSorted[0].order
        });
    };

    const fetchData = async (options = {}) => {
        if (!token) return;

        try {
            const params = {
                page: options.page || page,
                page_size: options.pageSize || pageSize,
                date_from: dateToLocalISO(dateFrom) || null,
                date_to: dateToLocalISO(dateTo) || null,
                time_zone: timeZone.label,
                status: options.status !== undefined ? options.status : stateFilter.value,
                sort: options.sortField || defaultSorted[0].dataField,
                order: options.sortOrder || defaultSorted[0].order
            };

            if (options.search !== undefined) {
                params.search = options.search;
            } else if (searchTerm) {
                params.search = searchTerm;
            }

            const result = await _campaignsGet(token, params);
            if (result.error) {
                setError(result.error);
            } else {
                setCampaigns(result.campaigns);
                setTotalItems(result.total_items);
                if (options.page) {
                    setPage(options.page);
                }
                if (options.pageSize) {
                    setPageSize(options.pageSize);
                }
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
        switch (type) {
            case 'pagination':
                setPage(page);
                setPageSize(sizePerPage);
                fetchData({ page, pageSize: sizePerPage });
                break;
            case 'sort':
                fetchData({ sortField, sortOrder });
                break;
            default:
                break;
        }
    };

    const dateToLocalISO = (date) => {
        let timeZoneOffset = (new Date(date)).getTimezoneOffset() * 60000;
        let localISOTime = (new Date(date - timeZoneOffset)).toISOString();
        return localISOTime;
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        setPage(1);
        fetchData({
            page: 1,
            pageSize,
            search: value
        });
    };

    const handleDeleteCampaign = async (id) => {
        await _campaignDelete(token, id);
        fetchData();
        setShowDeleteDialog(false);
    };

    const handleStateFilter = (selectedOption) => {
        fetchData({
            page: 1,
            pageSize,
            status: selectedOption.value
        });
        setStateFilter(selectedOption);
    };

    const handleTimeZoneChange = (selectedOption) => {
        setTimeZone(selectedOption);
    };

    const viewFormatter = (cell) => (
        <Link className='campaignViewBtn' to={`/campaigns/${cell}`}>
            <FontAwesomeIcon icon={regular('eye')} />
        </Link>
    );

    const startAtFormatter = (cell) => moment(new Date(cell)).format('yyyy-MM-DD H:mm');

    const stateFormatter = (cell) => {
        const statusMap = {
            0: 'Draft',
            1: 'Active',
            2: 'In Progress',
            3: 'Finished'
        };
        return statusMap[cell] || '';
    };

    const editFormatter = (cell, row) => (
        <EditLink
            to={`/campaigns/edit-campaign/${Number(row.ID)}`}
            condition={!(row.status === 2 || row.status === 3)}
            conditionAction={() => { toast.error('Campaigns with the status "In Progress" or "Finished" cannot be edited.'); }}>
            <FontAwesomeIcon icon={solid('pencil')} />
        </EditLink>
    );

    const deleteFormatter = (cell, row) => (
        <button
            className='tableIconBtn trashBtn'
            onClick={() => {
                if (row.status === 2 || row.status === 3) {
                    toast.error('Campaigns with the status "In Progress" or "Finished" cannot be deleted.');
                } else {
                    setCampaignToDelete(row);
                    setShowDeleteDialog(true);
                }
            }}>
            <FontAwesomeIcon icon={regular('trash-can')} />
        </button>
    );

    const columns = [
        {
            dataField: 'ID',
            text: 'View',
            classes: 'iconColumn',
            formatter: viewFormatter
        },
        {
            dataField: 'name',
            text: 'Campaign',
            sort: true
        },
        {
            dataField: 'start_at',
            text: 'Scheduled',
            formatter: startAtFormatter,
            sort: false
        },
        {
            dataField: 'status',
            text: 'State',
            formatter: stateFormatter,
            sort: false
        },
        {
            dataField: 'template_id',
            text: 'Edit',
            classes: 'iconColumn',
            formatter: editFormatter
        },
        {
            dataField: 'user_id',
            text: 'Delete',
            classes: 'iconColumn',
            formatter: deleteFormatter
        }
    ];

    const pagination = paginationFactory({
        page,
        sizePerPage: pageSize,
        totalSize: totalItems,
        showTotal: true,
        hidePageListOnlyOnePage: true,
        hideSizePerPage: totalItems <= 10
    });

    const noData = () => <p className='emptyTableMessage'>You have no campaigns yet! Create it</p>;

    const dateButtonsBlock = (
        <div className='dateButtonsBlock'>
            <button
                id='today'
                className='mainBtn btn'
                onClick={() => {
                    const todayStart = new Date(new Date().setHours(0, 0, 0, 0));
                    handleDateRangeChange(todayStart, new Date());
                }}>Today</button>
            <button
                id='yesterday'
                className='mainBtn btn'
                onClick={() => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    yesterday.setHours(0, 0, 0, 0);

                    const yesterdayEnd = new Date(yesterday);
                    yesterdayEnd.setHours(23, 59, 59, 999);

                    handleDateRangeChange(yesterday, yesterdayEnd);
                }}>Yesterday</button>
            <button
                id='lastHour'
                className='mainBtn btn'
                onClick={() => {
                    const lastHour = new Date();
                    lastHour.setHours(lastHour.getHours() - 1);
                    handleDateRangeChange(lastHour, new Date());
                }}>Last Hour</button>
        </div>
    );

    return (
        <div className='content wideContent'>
            <div>
                <div className='pageTitle'>
                    <span>Campaigns</span>
                </div>
                {error && <p className='msgBlock errorMsg'>{error}</p>}
                <div className='row'>
                    <div className='col-lg-12'>
                        <div className='myTable'>
                            <div className='tableHeader'>
                                <div className='mb-3 tableHeaderInline dateFilterWithBtns'>
                                    <div className='tableFiltersCol'>
                                        <div className='filterCol'>
                                            <label className='inputLabel'>From</label>
                                            <DatePicker
                                                selected={dateFrom}
                                                onChange={(date) => handleDateChange(date, true)}
                                                selectsStart
                                                showTimeSelect
                                                dateFormat="yyyy-MM-dd H:mm"
                                                timeFormat='H:mm'
                                                maxDate={new Date()}
                                                minTime={new Date(startOfTheDay)}
                                                maxTime={maxStartTime}
                                                className='formInput datePicker'
                                            />
                                        </div>
                                        <div className='filterCol'>
                                            <label className='inputLabel'>To</label>
                                            <DatePicker
                                                selected={dateTo}
                                                onChange={(date) => handleDateChange(date, false)}
                                                selectsEnd
                                                showTimeSelect
                                                dateFormat="yyyy-MM-dd H:mm"
                                                timeFormat='H:mm'
                                                minDate={dateFrom}
                                                minTime={dateFrom.getDate() < dateTo.getDate() ?
                                                    new Date(startOfTheDay) :
                                                    new Date(dateFrom)
                                                }
                                                maxTime={new Date().setHours(23, 59, 59, 999)}
                                                className='formInput datePicker'
                                            />
                                        </div>
                                    </div>
                                    {dateButtonsBlock}
                                </div>
                                <div className="mb-3">
                                    <Select
                                        id='timeZones'
                                        name='timeZones'
                                        className='customSelect'
                                        classNamePrefix='select'
                                        options={timeZones}
                                        value={timeZone}
                                        onChange={handleTimeZoneChange}
                                    />
                                </div>
                                <div className="tableHeaderInline">
                                    <div className="tableFiltersCol">
                                        <div className='filterCol'>
                                            <label className='inputLabel' htmlFor='campaignFilter'>
                                                Campaign
                                            </label>
                                            <input
                                                id='campaignFilter'
                                                name='campaignFilter'
                                                type='text'
                                                className='searchInput'
                                                placeholder='Search...'
                                                value={searchTerm}
                                                onChange={(event) => handleSearch(event.target.value)}
                                            />
                                        </div>
                                        <div className='filterCol'>
                                            <label className='inputLabel' htmlFor='state'>
                                                State
                                            </label>
                                            <Select
                                                id='state'
                                                name='state'
                                                className='customSelect'
                                                classNamePrefix='select'
                                                options={states}
                                                value={stateFilter}
                                                onChange={handleStateFilter}
                                            />
                                        </div>
                                    </div>
                                    <div className='tableBtnCol'>
                                        <Link className='mainBtn btn' to='/create-campaign'>
                                            Create Campaign
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <BootstrapTable
                                keyField='ID'
                                data={campaigns}
                                columns={columns}
                                wrapperClasses='table-responsive'
                                bordered={false}
                                pagination={pagination}
                                defaultSorted={defaultSorted}
                                noDataIndication={noData}
                                remote={{ pagination: true, sort: true }}
                                onTableChange={handleTableChange}
                            />
                        </div>
                    </div>
                </div>
                <ModalDialog
                    show={showDeleteDialog}
                    onHide={() => setShowDeleteDialog(false)}
                    onAccept={() => handleDeleteCampaign(campaignToDelete.ID)}
                    title={`Delete Campaign ${campaignToDelete.name}`}
                    acceptBtnText="Delete">
                    <span>{`Are you sure you want to delete campaign '${campaignToDelete.name}'?`}</span>
                </ModalDialog>
            </div>
            <Toaster />
        </div >
    );
};